import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Button } from 'react-bootstrap';
import { FaPlay, FaPause, FaDownload, FaVolumeUp } from '../../../../icons';
import WaveSurfer from 'wavesurfer.js';
import { motion } from 'framer-motion';

// Your interface definition
interface AudioWavePlayerProps {
  audioUrl: string;
  className?: string;
  showDownload?: boolean;
  showVolumeIcon?: boolean;
  height?: number;
  waveColor?: string;
  progressColor?: string;
  cursorColor?: string;
}

const AudioWavePlayer: React.FC<AudioWavePlayerProps> = ({
  audioUrl,
  className = '',
  showDownload = true,
  showVolumeIcon = true,
  height = 60,
  waveColor = '#99CAEB',
  progressColor = '#3957A0',
  cursorColor = '#D7A360'
}) => {
  const waveformRef = useRef<HTMLDivElement>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // --- LOGIC: This is the corrected useEffect from our previous discussion ---
  useEffect(() => {
    if (!waveformRef.current) return;
    if (!audioUrl) {
      setError('No audio URL provided');
      setIsLoading(false);
      return;
    }

    let isMounted = true;

    setIsLoading(true);
    setError(null);
    setCurrentTime(0);
    setDuration(0);

    const ws = WaveSurfer.create({
      container: waveformRef.current,
      height,
      waveColor,
      progressColor,
      cursorColor,
      barWidth: 2,
      barRadius: 3,
      normalize: true,
    });
    wavesurferRef.current = ws;

    ws.on('ready', (newDuration) => {
      if (isMounted) {
        setDuration(newDuration);
        setIsLoading(false);
      }
    });

    ws.on('error', (err) => {
      if (isMounted) {
        setError('Failed to load audio. The file may be corrupt or inaccessible.');
        setIsLoading(false);
        console.error('WaveSurfer error:', err);
      }
    });

    ws.on('timeupdate', (time) => { if (isMounted) setCurrentTime(time); });
    ws.on('play', () => { if (isMounted) setIsPlaying(true); });
    ws.on('pause', () => { if (isMounted) setIsPlaying(false); });

    ws.load(audioUrl);

    return () => {
      isMounted = false;
      ws.destroy();
    };
  }, [audioUrl, height, waveColor, progressColor, cursorColor]);

  // --- Your Callback and Formatting Functions ---
  const togglePlayback = useCallback(() => {
    if (wavesurferRef.current) {
      wavesurferRef.current.playPause();
    }
  }, []);

  const downloadAudio = useCallback(() => {
    if (audioUrl) {
      const link = document.createElement('a');
      link.href = audioUrl;
      link.download = `audio-message-${Date.now()}.webm`;
      link.click();
    }
  }, [audioUrl]);

  const formatTime = (seconds: number): string => {
    if (isNaN(seconds) || seconds < 0) return '00:00';
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // --- RENDER LOGIC: This is your full UI, now connected to the working logic ---

  if (error && !isLoading) {
    return (
      <motion.div 
        className={`audio-wave-player error ${className}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        // ... your error styling
      >
        <div className="error-message text-center">
          <div className="mb-2">
            <span className="text-danger" style={{ fontSize: '1.2rem' }}>⚠️</span>
          </div>
          <div className="mb-2">
            <strong className="text-danger">{error}</strong>
          </div>
          <div className="mt-2">
            <small className="text-muted">
              URL: <code style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>{audioUrl}</code>
            </small>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div 
      className={`audio-wave-player-enhanced ${className}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
      style={{
        background: 'linear-gradient(135deg, #f8f9ff 0%, #e7ecf1 100%)',
        borderRadius: '16px',
        padding: '20px',
        border: '1px solid rgba(57, 87, 160, 0.1)',
        boxShadow: '0 4px 20px rgba(57, 87, 160, 0.08)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Subtle background pattern */}
      <div 
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'radial-gradient(circle at 20% 80%, rgba(57, 87, 160, 0.03) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(215, 163, 96, 0.03) 0%, transparent 50%)',
          pointerEvents: 'none'
        }}
      />
      
      <div className="audio-player-header mb-3 d-flex align-items-center justify-content-between">
        <div className="audio-indicator d-flex align-items-center gap-2">
          <motion.div
            animate={isPlaying ? { scale: [1, 1.1, 1] } : {}}
            transition={{ duration: 0.6, repeat: isPlaying ? Infinity : 0 }}
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: isPlaying 
                ? '#3957A0' 
                : '#A5A5A5'
            }}
          />
          <span style={{ 
            fontSize: '14px', 
            fontWeight: '600', 
            color: '#3957A0',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            {isLoading ? 'Loading...' : isPlaying ? 'Now Playing' : 'Audio Message'}
          </span>
        </div>
        
        {!isLoading && duration > 0 && (
          <motion.div 
            className="audio-time"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{
              background: 'rgba(57, 87, 160, 0.1)',
              padding: '4px 12px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '600',
              color: '#3957A0'
            }}
          >
            <span className="current-time">
              {formatTime(currentTime)}
            </span>
            <span style={{ margin: '0 4px', opacity: 0.6 }}>/</span>
            <span className="total-time">
              {formatTime(duration)}
            </span>
          </motion.div>
        )}
      </div>

      <motion.div 
        className="waveform-container mb-3"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2, duration: 0.4 }}
        style={{
          background: 'rgba(255, 255, 255, 0.8)',
          borderRadius: '12px',
          padding: '16px',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(57, 87, 160, 0.1)'
        }}
      >
        {isLoading && (
          <div className="loading-placeholder d-flex align-items-center justify-content-center" style={{ height: `${height}px` }}>
            <motion.div 
              className="d-flex align-items-center gap-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                style={{
                  width: '24px',
                  height: '24px',
                  border: '3px solid rgba(57, 87, 160, 0.1)',
                  borderTop: '3px solid #3957A0',
                  borderRadius: '50%'
                }}
              />
              <span style={{ 
                color: '#3957A0', 
                fontWeight: '500',
                fontSize: '14px' 
              }}>
                Loading audio waveform...
              </span>
            </motion.div>
          </div>
        )}
        
        <div ref={waveformRef} style={{ 
          minHeight: `${height}px`, 
          display: isLoading ? 'none' : 'block',
          borderRadius: '8px',
          overflow: 'hidden'
        }} />
      </motion.div>

      <div className="playback-controls d-flex align-items-center justify-content-between gap-3">
        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <motion.button
            onClick={togglePlayback}
            disabled={isLoading}
            style={{
              background: isPlaying 
                ? '#D7A360' 
                : '#3957A0',
              border: 'none',
              borderRadius: '50px',
              padding: '12px 24px',
              color: 'white',
              fontWeight: '600',
              fontSize: '14px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.6 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 4px 15px rgba(57, 87, 160, 0.3)',
              transition: 'all 0.3s ease'
            }}
            whileHover={{
              boxShadow: '0 6px 20px rgba(215, 163, 96, 0.4)',
              y: -2,
              background: isPlaying 
                ? '#D7A360' 
                : '#D7A360'
            }}
          >
            {isLoading ? (
              <>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  style={{
                    width: '14px',
                    height: '14px',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    borderTop: '2px solid white',
                    borderRadius: '50%'
                  }}
                />
                <span>Loading</span>
              </>
            ) : (
              <>
                <motion.div
                  animate={isPlaying ? { scale: [1, 1.1, 1] } : {}}
                  transition={{ duration: 0.3 }}
                >
                  {isPlaying ? <FaPause size={14} /> : <FaPlay size={14} />}
                </motion.div>
                <span>{isPlaying ? 'Pause' : 'Play'}</span>
              </>
            )}
          </motion.button>
        </motion.div>

        <div className="secondary-controls d-flex align-items-center gap-2">
          {/* Volume Icon - Commented Out */}
          {/* {showVolumeIcon && (
            <motion.div 
              initial={{ opacity: 0, x: -10 }} 
              animate={{ opacity: 1, x: 0 }} 
              transition={{ delay: 0.3 }}
              style={{
                padding: '8px 12px',
                background: 'rgba(57, 87, 160, 0.1)',
                borderRadius: '25px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <motion.div
                animate={isPlaying ? { scale: [1, 1.2, 1] } : {}}
                transition={{ duration: 0.8, repeat: isPlaying ? Infinity : 0 }}
              >
                <FaVolumeUp size={14} style={{ color: '#3957A0' }} />
              </motion.div>
              <span style={{ 
                fontSize: '12px', 
                fontWeight: '500', 
                color: '#3957A0' 
              }}>
                Audio
              </span>
            </motion.div>
          )} */}

          {/* Download Button - Commented Out */}
          {/* {showDownload && (
            <motion.button
              onClick={downloadAudio}
              whileHover={{ scale: 1.05, y: -2 }} 
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, x: -10 }} 
              animate={{ opacity: 1, x: 0 }} 
              transition={{ delay: 0.4 }}
              style={{
                background: 'rgba(215, 163, 96, 0.1)',
                border: '1px solid rgba(215, 163, 96, 0.2)',
                borderRadius: '25px',
                padding: '8px 16px',
                color: '#D7A360',
                fontWeight: '500',
                fontSize: '13px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(215, 163, 96, 0.15)';
                e.target.style.borderColor = 'rgba(215, 163, 96, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(215, 163, 96, 0.1)';
                e.target.style.borderColor = 'rgba(215, 163, 96, 0.2)';
              }}
            >
              <FaDownload size={12} />
              <span>Download</span>
            </motion.button>
          )} */}
        </div>
      </div>
    </motion.div>
  );
};

export default AudioWavePlayer;
